// How the app tells someone something went wrong or needs their attention —
// one look on every screen (#444). Red: something they tried didn't work.
// Amber: something to know before going on; nothing failed. Either sits where
// it happened and stays until it's fixed or tried again — there's no OK to tap.
// Tinted box, border, icon and dark text: still readable on a dimmed iPad.
import type { ReactNode } from 'react';
import { CircleAlert, TriangleAlert } from 'lucide-react';

const TONES = {
  error: { colours: 'border-red-300 bg-red-50 text-red-800', Icon: CircleAlert, role: 'alert' },
  warning: { colours: 'border-amber-300 bg-amber-50 text-amber-900', Icon: TriangleAlert, role: 'status' },
} as const;

interface Props {
  children: ReactNode;
  className?: string;
}

function Message({ tone, children, className = '' }: Props & { tone: keyof typeof TONES }) {
  const { colours, Icon, role } = TONES[tone];
  return (
    <div role={role} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${colours} ${className}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

/** Something the person tried didn't work. */
export const ErrorMessage = (props: Props) => <Message tone="error" {...props} />;

/** Something to know before going on; nothing failed. */
export const Notice = (props: Props) => <Message tone="warning" {...props} />;
